import React, {
  useState, useContext, useRef, useEffect, useMemo, forwardRef, useImperativeHandle,
} from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import { LeadContext } from 'Leads/Details/hooks/context';
import { AuthContext } from 'api/context';
import AuthManager, { client } from 'api/auth';
import DesignTool from 'Leads/Details/DesignTool';
import Loading from 'components/Loading';
import IFrameLogo from 'components/IFrameLogo';
import { postToParent } from './postMessage';

const Designer = forwardRef(({ visible, callback, context, companyName, designFrame }, ref) => {
  const { uid, lead, newEndpoint, predictAPI } = useContext(LeadContext);
  const { displaySnack, getTaskStatus } = useContext(AuthContext);
  const [auth] = useState(AuthManager);

  const newLeadApi = newEndpoint('iframe/lead/');

  const designer = useRef();
  const history = useHistory();
  const debugEnabled = useMemo(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const debugParam = params.get('debug');
      if (debugParam === 'true' || debugParam === '1') {
        return true;
      }
      return window.sessionStorage?.getItem('iframe.debugTelemetry') === '1';
    } catch (err) {
      return false;
    }
  }, []);

  const appendDebugParam = (url) => {
    if (!debugEnabled || !url) return url;
    if (url.includes('debug=')) return url;
    return url.includes('?') ? `${url}&debug=true` : `${url}?debug=true`;
  };

  const parseDatasetKeyFromPath = (path) => {
    try {
      if (!path || typeof path !== 'string') return null;
      const base = path.split('/').pop() || '';
      if (!base.endsWith('.csv')) return null;
      return base.slice(0, -'.csv'.length) || null;
    } catch (err) {
      return null;
    }
  };

  const isWeatherDatasetReady = (selected, fetchedPath) => {
    const selectedKey = typeof selected === 'string' ? selected : null;
    const fetchedKey = parseDatasetKeyFromPath(fetchedPath);
    if (!selectedKey) return Boolean(fetchedKey);
    return fetchedKey === selectedKey;
  };

  const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  const pollWeatherDatasetStatus = async (datasetKey, taskId) => {
    const endpoint = newEndpoint('api/nsrdb/status/');
    if (!endpoint || !lead?.uid) {
      throw new Error('Weather dataset status endpoint unavailable');
    }
    const start = Date.now();
    let attempt = 0;
    const timeoutMs = 120000;

    while (Date.now() - start < timeoutMs) {
      attempt += 1;
      const query = taskId
        ? `?task_id=${encodeURIComponent(taskId)}`
        : `?uid=${encodeURIComponent(lead?.uid)}&dataset=${encodeURIComponent(
            datasetKey
          )}`;
      // eslint-disable-next-line no-await-in-loop
      const res = await endpoint.list(query);
      if (res?.status === 'ready' && res?.weather_dataset) {
        designer.current.handleWeatherDatasetFetched(res.weather_dataset);
        return true;
      }
      if (res?.status === 'failed' || res?.success === false) {
        throw new Error(res?.error || 'Failed to fetch weather dataset');
      }
      const delayMs = Math.min(1000 + attempt * 500, 5000);
      // eslint-disable-next-line no-await-in-loop
      await wait(delayMs);
    }

    throw new Error('Timed out waiting for weather dataset');
  };

  const ensureWeatherDatasetReady = async () => {
    if (!designer.current) return false;
    const selectedDataset = designer.current.state?.weatherDatasetSelected;
    const fetchedPath = designer.current.state?.weatherDatasetFetchedPath;
    if (isWeatherDatasetReady(selectedDataset, fetchedPath)) return true;

    const datasetKey = selectedDataset || 'nsrdb_goes_v4_2024';
    const endpoint = newEndpoint('api/nsrdb/fetch/');
    if (!endpoint || !lead?.uid) {
      return false;
    }
    const res = await endpoint.create({ uid: lead?.uid, dataset: datasetKey });
    if (!res || res.success === false) {
      throw new Error(res?.error || 'Failed to fetch weather dataset');
    }
    if (res?.weather_dataset) {
      designer.current.handleWeatherDatasetFetched(res.weather_dataset);
      return true;
    }
    if (res?.status === 'pending') {
      return pollWeatherDatasetStatus(datasetKey, res.task_id);
    }
    return true;
  };

  const [createLeadInterval, setCreateLeadInterval] = useState();
  const [runAiInterval, setRunAiInterval] = useState();
  const [fetchingImagery, setFetchingImagery] = useState();

  const designerCallback = (callbackType, data) => {
    postToParent(callbackType, data);
    callback(callbackType, data);
  };

  const getDesignerMethod = (methodName) => {
    const handler = designer.current?.[methodName];
    return typeof handler === 'function' ? handler : null;
  };

  const callDesignerMethod = (methodName, ...args) =>
    getDesignerMethod(methodName)?.(...args);

  const callRequiredDesignerMethod = (methodName, ...args) => {
    const handler = getDesignerMethod(methodName);
    if (!handler) {
      throw new Error(`Designer method "${methodName}" is not ready`);
    }
    return handler(...args);
  };

  const waitForMethods = async (
    requiredMethods = [],
    timeoutMs = 15000,
    pollIntervalMs = 100
  ) => {
    const startedAt = Date.now();
    while (Date.now() - startedAt < timeoutMs) {
      const instance = designer.current;
      const ready =
        instance &&
        requiredMethods.every(
          (methodName) => typeof instance?.[methodName] === 'function'
        );
      if (ready) return instance;
      await new Promise((resolve) => setTimeout(resolve, pollIntervalMs));
    }
    throw new Error('Timed out waiting for designer methods');
  };

  const displayValidationErrors = (errors) => {
    const err = String(errors);
    let errMsg = err.split('At path:')[1];
    errMsg = errors.message.split('--')[1].trim();
    if (errors.key === 'coordinates') {
      errMsg = errMsg.replace('`/^((-?|\\+?)?\\d+(\\.\\d+)?),\\s*((-?|\\+?)?\\d+(\\.\\d+)?)$/`', '`lat, lng` format');
    }
    const msg = `${errors.key}: ${errMsg}`;
    // displaySnack({ variant: 'danger', message: msg });
    designerCallback('Error', { code: 11, message: msg });
  };

  const setLead = (leadId) => {
    localStorage.setItem('lead-id', leadId);
  };

  const getLead = () => {
    const leadId = localStorage.getItem('lead-id');
    return leadId;
  };

  const eraseLead = () => {
    localStorage.setItem('lead-id', null);
  };

  const trackTask = (taskId) => {
    if (taskId === undefined) return;
    getTaskStatus(taskId).then(({ state }) => {
      console.log(taskId, state);
      if (state === 'STARTED') return;
      setCreateLeadInterval((interval) => clearInterval(interval));
      setRunAiInterval((interval) => clearInterval(interval));
      if (state === 'SUCCESS') {
        // displaying lead id after the task has finished
        const leadId = getLead();
        // displaySnack({ variant: 'info', message: `Lead created: ${leadId}` });
        designerCallback('NewLead', leadId);
        eraseLead();
      } else { // PENDING / FAILURE
        // displaySnack({ variant: 'danger', message: 'An error was encountered.' });
        designerCallback('Error', { code: 20, message: 'Could not finish the design' });
      }
    });
  };

  const loadNewLead = (id, chain = false) => {
    // only used when NewLead is handled in iframe
    const newUrl = appendDebugParam(
      `/iframe/${id}?chain=${chain ? 'true' : 'false'}`
    );
    designerCallback('Navigation', newUrl);
    window.location.replace(newUrl);
    // history.push(`/iframe/${id}`);
  };

  const isCurrentLead = (id) =>
    visible && String(id || '') === String(uid || '');

  const loadLeadPanelPlacement = (id) => {
    if (isCurrentLead(id)) {
      return false;
    }
    history.push(appendDebugParam(`/iframe/${id}`));
    return true;
  };

  const loadLead = (id) => {
    // used in designer iframe to load a lead
    const newUrl = appendDebugParam(`/iframe/${id}?design=true`);
    designerCallback('Navigation', newUrl);
    window.location.replace(newUrl);
  };

  const checkCoverage = async ({ coordinates }) => {
    const api_key = auth.getApiKey();
    const headers = {
      Authorization: `Api-Key ${api_key}`,
    };

    try {
      const data = await client.post(
        'iframe/coverage/',
        {
          coords: coordinates,
        },
        {
          headers,
          timeout: 45000,
        }
      );
      const res = data.data;
      designerCallback('coverage', res);
      return res;
    } catch (err) {
      const code =
        Number(err?.response?.data?.code) ||
        (err?.code === 'ECONNABORTED' ? 504 : 500);
      const message =
        err?.response?.data?.msg ||
        err?.response?.data?.message ||
        err?.message ||
        'Error in coverage check';
      console.log(`iframe coverage error [${code}]: ${message}`);
      designerCallback('coverage', false);
      return false;
    }
  };

  const newLead = ({
    contacts, coordinates, electricity_usage, property_profile, utility, monthly_bill, type = 'AI', rgb = '', dsm = '', resolution = 7.5,
  }) => {
    console.log('inputs:', coordinates, electricity_usage);

    /**
     * This is where Api-Key is formatted in request header
     * The Api key is assumably arg of one of iFrame methods, and its value should be accessible here.
     */

    const api_key = auth.getApiKey();
    const headers = {
      Authorization: `Api-Key ${api_key}`,
    };

    setFetchingImagery(true);

    client.post('iframe/lead/', {
      contacts, coordinates, electricity_usage, utility, monthly_bill, type, rgb, dsm, resolution, property_profile
    }, {
      headers,
    })
      .then((data) => {
        setFetchingImagery(false);
        const res = data.data;
        setLead(res.lead);
        // display task-related UI
        if (res.task) {
          // displaySnack({ variant: 'info', message: 'Creating lead' });
          const interval = setInterval(() => trackTask(res.task), 1500);
          setCreateLeadInterval(interval);
        } else {
          const leadId = res.lead;
          // displaySnack({ variant: 'info', message: `Lead created: ${leadId}` });
          designerCallback('NewLead', leadId);
          setTimeout(loadLead(leadId), 1000);
        }
      }).catch((err) => {
        setFetchingImagery(false);
        // displaySnack({ variant: 'danger', message: 'Error in creating lead' });
        try {
          console.log(err.response);
          designerCallback('Error', { code: err.response.data.code, message: err.response.data.msg });
        } catch (responseErr) {
          console.log('after error in django:', responseErr);
        }
      });
  };

  const runPrediction = (params) => {
    const request = {
      uid: params.uid,
      design_type: 'A',
      no_refinement: 'F',
    };

    const api_key = auth.getApiKey();
    const headers = {
      Authorization: `Api-Key ${api_key}`,
    };

    client.post('crm/lead/predict/', request, { headers })
      .then((data) => {
        const res = data.data;
        setLead(params.uid);
        // display task-related UI
        if (res.task) {
          // displaySnack({ variant: 'info', message: 'Processing lead' });
          const interval = setInterval(() => trackTask(res.task), 1500);
          setRunAiInterval(interval);
        }
      }).catch(() => {
        // displaySnack({ variant: 'danger', message: 'Prediction not started.' });
      });
  };

  const handleMessage = async (event) => {
    const { cmd, params = null, config = null } = event.data;
    const designerCommandsWithoutTool = new Set([
      'designer.LoadLead',
      'designer.NewLead',
    ]);

    if (
      cmd?.startsWith('designer.') &&
      !designerCommandsWithoutTool.has(cmd) &&
      !designer.current
    ) {
      designerCallback('Error', {
        code: '50',
        message:
          'Designer tool is not available in the current iframe mode. Open Designer Mode before calling designer methods.',
      });
      return;
    }

    switch (cmd) {
      case 'designer.LoadLead':
        if (params) {
          loadLead(params.uid);
        }
        break;
      case 'designer.NewLead':
        if (params) newLead(params);
        break;
      case 'designer.FullScreen':
        if (designer.current) designer.current.goFullScreen(true);
        break;
      case 'designer.2D':
        if (designer.current) designer.current.switchView('2D');
        break;
      case 'designer.3D':
        if (designer.current) designer.current.switchView('3D');
        break;
      case 'designer.3DJson':
        if (designer.current) designer.current.handleDownload3DJSON();
        break;
      case 'designer.PanelPlacement':
        if (designer.current) {
          designer.current.handleCurrentAccordion('revise');
          designer.current.handlePlacement(params);
        }
        break;
      case 'designer.Shading':
        if (designer.current) {
          designer.current.handleCurrentAccordion('shading');
          try {
            await ensureWeatherDatasetReady();
          } catch (err) {
            designerCallback('Error', {
              code: 52,
              message: err?.message || 'Failed to fetch weather dataset',
            });
            break;
          }
          designer.current.handleShading();
        }
        break;
      case 'designer.Measure':
        if (designer.current) {
          designer.current.handleCurrentAccordion('segments');
          designer.current.handleMeasure();
        }
        break;
      case 'designer.EdgeLabeling':
        if (designer.current) {
          designer.current.handleCurrentAccordion('segments');
          designer.current.runEdgeDetectionHandler(true);
        }
        break;
      case 'designer.Optimize':
        if (designer.current) {
          designer.current.handleCurrentAccordion('segments');
          designer.current.runOptimizationHandler(false);
        }
        break;
      case 'designer.Clear':
        if (!designer.current) break;
        designer.current.handleCurrentAccordion('segments');
        designer.current.clearPanels();
        designer.current.clearMeasurment();
        designer.current.clearLabelsHandler();
        break;
      case 'designer.CreateProposal':
        if (!designer.current) break;
        try {
          await designer.current.handleProposal(params, false, {
            requireProduction: false,
          });
        } catch (err) {
          designerCallback('Error', {
            code: '53',
            message: err?.message || 'Error in creating proposal',
          });
        }
        break;
      case 'designer.ShadingValues':
        if (!designer.current) break;
        designer.current.shadingValues();
        break;
      case 'designer.ClearPanels':
        if (!designer.current) break;
        designer.current.clearPanels();
        break;
      case 'designer.PlaceGroundMount':
        if (!designer.current) break;
        designer.current.placeGroundMount(params.gridOption, params.segmentOption, params.panelOptions);
        break;
      case 'designer.TranslateCanvas':
        if (!designer.current) break;
        designer.current.translateCanvas(params.offsetX, params.offsetY, params.flipX, params.flipY, params.translateRoofs, params.translateObstacles, params.translateTrees);
        break;
      case 'designer.CalculateProduction':
      case 'designer.calculateProduction':
        if (!designer.current) break;
        {
          const productionSettings = {
            ...(config && typeof config === 'object' ? config : {}),
            ...(params && typeof params === 'object' ? params : {}),
          };
          const onlySelectedPanels = productionSettings?.onlySelectedPanels;
          const options = {
            waitForPanelShadingTextures: false,
            ...(typeof onlySelectedPanels === 'boolean'
              ? { onlySelectedPanels, persistSelectionState: false }
              : {}),
          };
          if (
            Object.keys(productionSettings).length > 0 &&
            designer.current.applyProductionSettings
          ) {
            await designer.current.applyProductionSettings(productionSettings);
          }
          await designer.current.handleSolarProduction(options);
        }
        break;
      default:
        break;
    }
  };

  useImperativeHandle(ref, () => ({
    uid,
    lead,
    designer,
    handleMessage,
    checkCoverage,
    newLead,
    displayValidationErrors,
    loadLead,
    loadNewLead,
    loadLeadPanelPlacement,
    isCurrentLead,
    waitForMethods,
    runPrediction,
    checkIsLoaded: (...args) => callDesignerMethod('checkIsLoaded', ...args),
    checkIsShadingRunning: (...args) =>
      callDesignerMethod('checkIsShadingRunning', ...args),
    checkIsPVWattsHandled: (...args) =>
      callDesignerMethod('checkIsPVWattsHandled', ...args),
    handleCurrentAccordion: (...args) =>
      callDesignerMethod('handleCurrentAccordion', ...args),
    handlePlacement: (...args) => callDesignerMethod('handlePlacement', ...args),
    handlePVWatts: (...args) => callDesignerMethod('handlePVWatts', ...args),
    handleShading: (...args) => callDesignerMethod('handleShading', ...args),
    handleSolarProduction: (...args) =>
      callRequiredDesignerMethod('handleSolarProduction', ...args),
    applyProductionSettings: (...args) =>
      callRequiredDesignerMethod('applyProductionSettings', ...args),
    handleMeasure: (...args) => callDesignerMethod('handleMeasure', ...args),
    runEdgeDetectionHandler: (...args) =>
      callDesignerMethod('runEdgeDetectionHandler', ...args),
    runOptimizationHandler: (...args) =>
      callDesignerMethod('runOptimizationHandler', ...args),
    handleProposal: (...args) => callDesignerMethod('handleProposal', ...args),
    clearPanels: (...args) => callDesignerMethod('clearPanels', ...args),
    placeGroundMount: (...args) =>
      callDesignerMethod('placeGroundMount', ...args),
    translateCanvas: (...args) =>
      callDesignerMethod('translateCanvas', ...args),
  }));

  const iframeDesigner = useMemo(() => {
    if (!lead) return null;
    return (<DesignTool ref={designer} context={context} iframe designFrame={designFrame} companyName={companyName} callback={designerCallback} />);
  }, [uid, lead, context]);

  if (fetchingImagery || createLeadInterval || runAiInterval) {
    return (
      <>
        <Loading title="Processing..." />
        {(companyName !== 'Astrawatt') && (companyName !== 'demand-iq') && <IFrameLogo textColor="dark" />}
      </>
    );
  }
  if (!visible) return null;
  return iframeDesigner;
});

Designer.propTypes = {
  visible: PropTypes.bool.isRequired,
};

export default Designer;
